import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import AdminService from "./AdminService";
import type {
    SablonaUdalosti,
    CreateSablonaRequest,
    UpdateSablonaRequest,
    VyhlasitZeSablonyRequest,
    Uzivatel,
    CreateUserRequest,
    UpdateUserRequest,
    SystemHealthResponse,
    ComponentHealth,
    AuditLog,
    PagedResponseAuditLog,
    AuditStatistics,
    CreateUdalostRequest,
    CreateUdalostResponse,
    TypUdalosti,
    PodtypUdalosti,
    PagedResponseUdalost,
    UdalostDetail,
    Udalost,
    AlertStatus,
    UserTestNotificationRequest,
    UserTestNotificationResponse,
    ResetTestLimitResponse,
    ScheduledTestPoplachResponse,
    BulkSetPinRequest,
    BulkDeletePinRequest,
} from "../generated";
import type { CreateUserResult } from "./AdminService";
import type { StatistikaResponse } from "../generated/models/StatistikaResponse";
import type { JednotkaDto } from "../generated/models/JednotkaDto";
import type { SearchUdalostiParams } from "../generated/models/SearchUdalostiParams";
import type { DashboardStats, StatsFilterParams } from "../generated/models/DashboardStats";
import type { AppConfigResponse } from "../generated/models/AppConfigResponse";
import JsdhNotifikaceService from "./JsdhNotifikaceService";
import { isMockActive } from "../../../utils/isMockActive";

// -----------------------------
// APP CONFIG QUERIES
// -----------------------------

export const useAppConfig = () => {
    return useQuery<AppConfigResponse>({
        queryKey: ['appConfig'],
        queryFn: () => JsdhNotifikaceService.getAppConfig(),
        staleTime: 5 * 60 * 1000, // 5 minut cache
        enabled: !isMockActive(), // ?mock= preview nesmí volat BE (vzor useJednotkaQuery, řádek 603)
    });
};

// -----------------------------
// USERS QUERIES
// -----------------------------

export const useAllUsersQuery = () => {
    return useQuery<Uzivatel[], Error>({
        queryKey: ["adminUsers"],
        queryFn: () => AdminService.getAllUsers(),
    });
};

export const useUserByIdQuery = (id: number) => {
    return useQuery<Uzivatel, Error>({
        queryKey: ["adminUser", id],
        queryFn: () => AdminService.getUserById(id),
        enabled: !!id,
    });
};

export const useAdminsQuery = () => {
    return useQuery<Uzivatel[], Error>({
        queryKey: ["adminUsers", "admins"],
        queryFn: () => AdminService.getAdmins(),
    });
};

// -----------------------------
// USERS MUTATIONS
// -----------------------------

export const useCreateUserMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<CreateUserResult, Error, CreateUserRequest>({
        mutationFn: (data) => AdminService.createUser(data),

        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            if (data?.user?.invitationEmailSent === true) {
                showToast(`Uživatel vytvořen, pozvánka odeslána na ${data.user.email}.`, "success");
            } else if (data?.user?.invitationEmailSent === false) {
                showToast("Uživatel vytvořen, ale pozvánku se nepodařilo odeslat. Zkus to znovu přes Upravit → Odeslat pozvánku.", "warning");
            } else {
                showToast("Uživatel byl úspěšně vytvořen.", "success");
            }
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit uživatele.", "error");
        },
    });
};

export const useUpdateUserMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Uzivatel, Error, { id: number; data: UpdateUserRequest }>({
        mutationFn: ({ id, data }) => AdminService.updateUser(id, data),

        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            queryClient.invalidateQueries({ queryKey: ["adminUser", id] });
            showToast("Uživatel byl úspěšně aktualizován.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat uživatele.", "error");
        },
    });
};

export const useDeleteUserMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => AdminService.deleteUser(id),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            showToast("Uživatel byl úspěšně smazán.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat uživatele.", "error");
        },
    });
};

export const useResetUserPasswordMutation = () => {
    const { showToast } = useToast();

    return useMutation<void, Error, { id: number; newPassword?: string; sendPasswordEmail?: boolean }>({
        mutationFn: ({ id, newPassword, sendPasswordEmail }) =>
            AdminService.resetUserPassword(id, newPassword, sendPasswordEmail),

        onSuccess: (_, variables) => {
            if (variables.sendPasswordEmail) {
                showToast("Odkaz na nastavení hesla byl odeslán na email.", "success");
            } else {
                showToast("Heslo bylo úspěšně resetováno.", "success");
            }
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se resetovat heslo.", "error");
        },
    });
};

export const useToggleUserStatusMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Uzivatel, Error, { id: number; aktivni: boolean }>({
        mutationFn: ({ id, aktivni }) => AdminService.toggleUserStatus(id, aktivni),

        onSuccess: (user) => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            queryClient.invalidateQueries({ queryKey: ["adminUser", user.id] });
            showToast(
                `Uživatel byl ${user.aktivni ? "aktivován" : "deaktivován"}.`,
                "success"
            );
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se změnit stav uživatele.", "error");
        },
    });
};

export const useChangeUserRoleMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Uzivatel, Error, { id: number; role: string }>({
        mutationFn: ({ id, role }) => AdminService.changeUserRole(id, role),

        onSuccess: (user) => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            queryClient.invalidateQueries({ queryKey: ["adminUser", user.id] });
            showToast("Role uživatele byla změněna.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se změnit roli uživatele.", "error");
        },
    });
};

export const useSendUserTestNotificationMutation = () => {
    const { showToast } = useToast();

    return useMutation<UserTestNotificationResponse, Error, { id: number; data?: UserTestNotificationRequest }>({
        mutationFn: ({ id, data }) => AdminService.sendUserTestNotification(id, data),

        onSuccess: (result) => {
            if (result.success) {
                showToast(result.message || "Testovací notifikace byla odeslána.", "success");
            } else {
                showToast(result.message || "Žádná notifikace nebyla odeslána.", "warning");
            }
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odeslat testovací notifikaci.", "error");
        },
    });
};

export const useResetUserTestLimitMutation = () => {
    const { showToast } = useToast();

    return useMutation<ResetTestLimitResponse, Error, number>({
        mutationFn: (id) => AdminService.resetUserTestLimit(id),

        onSuccess: (result) => {
            showToast(result.message || "Limit testů byl resetován.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se resetovat limit.", "error");
        },
    });
};

export const useUserTestHistoryQuery = (id: number | null) => {
    return useQuery<ScheduledTestPoplachResponse[], Error>({
        queryKey: ["adminUserTestHistory", id],
        queryFn: () => AdminService.getUserTestHistory(id!),
        enabled: !!id,
    });
};

export const useSendUserInvitationMutation = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    return useMutation({
        mutationFn: (id: number) => AdminService.sendInvitation(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            showToast("Pozvánka odeslána.", "success");
        },
        onError: (error: Error) => {
            // QueryError from handleRequest has .message (see axios.ts:45 / QueryError.ts:5).
            // BE response field `code` does NOT propagate (axios.ts doesn't pass it to QueryError).
            const message = error?.message || "Neznámá chyba";
            showToast(`Pozvánku se nepodařilo odeslat: ${message}`, "error");
        },
    });
};

// -----------------------------
// HEALTH QUERIES
// -----------------------------

export const useSystemHealthQuery = () => {
    return useQuery<SystemHealthResponse, Error>({
        queryKey: ["adminSystemHealth"],
        queryFn: () => AdminService.getSystemHealth(),
        refetchInterval: 30000, // Refresh every 30 seconds
    });
};

export const useDatabaseHealthQuery = () => {
    return useQuery<ComponentHealth, Error>({
        queryKey: ["adminDatabaseHealth"],
        queryFn: () => AdminService.getDatabaseHealth(),
    });
};

export const useEmailHealthQuery = () => {
    return useQuery<ComponentHealth, Error>({
        queryKey: ["adminEmailHealth"],
        queryFn: () => AdminService.getEmailHealth(),
    });
};

export const useFcmHealthQuery = () => {
    return useQuery<ComponentHealth, Error>({
        queryKey: ["adminFcmHealth"],
        queryFn: () => AdminService.getFcmHealth(),
    });
};

export const useTtsHealthQuery = () => {
    return useQuery<ComponentHealth, Error>({
        queryKey: ["adminTtsHealth"],
        queryFn: () => AdminService.getTtsHealth(),
    });
};

export const useMqttGatewayHealthQuery = () => {
    return useQuery<ComponentHealth, Error>({
        queryKey: ["adminMqttGatewayHealth"],
        queryFn: () => AdminService.getMqttGatewayHealth(),
    });
};

export const useDevicesHealthQuery = () => {
    return useQuery<ComponentHealth, Error>({
        queryKey: ["adminDevicesHealth"],
        queryFn: () => AdminService.getDevicesHealth(),
    });
};

// -----------------------------
// AUDIT QUERIES
// -----------------------------

interface AuditSearchParams {
    userId?: number;
    action?: string;
    entityType?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    size?: number;
}

export const useAuditLogsQuery = (params?: AuditSearchParams) => {
    return useQuery<PagedResponseAuditLog, Error>({
        queryKey: ["adminAuditLogs", params],
        queryFn: () => AdminService.searchAuditLogs(params),
    });
};

export const useRecentAuditLogsQuery = (limit = 50) => {
    return useQuery<AuditLog[], Error>({
        queryKey: ["adminRecentAuditLogs", limit],
        queryFn: () => AdminService.getRecentAuditLogs(limit),
    });
};

export const useAuditStatisticsQuery = () => {
    return useQuery<AuditStatistics, Error>({
        queryKey: ["adminAuditStatistics"],
        queryFn: () => AdminService.getAuditStatistics(),
    });
};

// -----------------------------
// AUDIT MUTATIONS
// -----------------------------

export const useCleanupAuditLogsMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ deletedCount: number }, Error, number>({
        mutationFn: (olderThanDays) => AdminService.cleanupAuditLogs(olderThanDays),

        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ["adminAuditLogs"] });
            queryClient.invalidateQueries({ queryKey: ["adminRecentAuditLogs"] });
            queryClient.invalidateQueries({ queryKey: ["adminAuditStatistics"] });
            showToast(`Smazáno ${result.deletedCount} záznamů auditu.`, "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vyčistit audit logy.", "error");
        },
    });
};

// -----------------------------
// KONFIGURACE MUTATIONS
// -----------------------------

export const useUpdateAdminKonfiguraceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { klic: string; hodnota: string }>({
        mutationFn: ({ klic, hodnota }) => AdminService.updateAdminKonfigurace(klic, hodnota),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["konfigurace"] });
            showToast("Konfigurace byla aktualizována.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat konfiguraci.", "error");
        },
    });
};

export const useTriggerRefreshMutation = () => {
    const { showToast } = useToast();

    return useMutation<void, Error, void>({
        mutationFn: () => AdminService.triggerRefresh(),

        onSuccess: () => {
            showToast("Refresh byl spuštěn.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se spustit refresh.", "error");
        },
    });
};

// -----------------------------
// UDÁLOSTI (POPLACHY) MUTATIONS
// -----------------------------

export const useCreateUdalostMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<CreateUdalostResponse, Error, CreateUdalostRequest>({
        mutationFn: (data) => AdminService.createUdalost(data),

        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["aktivniPoplach"] });
            queryClient.invalidateQueries({ queryKey: ["udalosti"] });
            showToast(response.message || "Poplach byl úspěšně vyhlášen.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vyhlásit poplach.", "error");
        },
    });
};

export const useDeleteUdalostMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => JsdhNotifikaceService.deleteUdalost(id),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["aktivniPoplach"] });
            queryClient.invalidateQueries({ queryKey: ["udalosti"] });
            queryClient.invalidateQueries({ queryKey: ["dashboardStats"] });
            showToast("Událost byla úspěšně smazána.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat událost.", "error");
        },
    });
};

export const useDeaktivujUdalostMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => JsdhNotifikaceService.deaktivujUdalost(id),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["aktivniPoplach"] });
            queryClient.invalidateQueries({ queryKey: ["udalosti"] });
            queryClient.invalidateQueries({ queryKey: ["dashboardStats"] });
            showToast("Poplach byl úspěšně deaktivován.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se deaktivovat poplach.", "error");
        },
    });
};

export const useAktivujUdalostMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<
        { udalost: Udalost; pocetOdeslanychNotifikaci: number; ttsVytvoreno: boolean; message: string },
        Error,
        { id: number; vytvorNovouUdalost?: boolean; odeslatNotifikace?: boolean; generovatTts?: boolean }
    >({
        mutationFn: ({ id, ...params }) => JsdhNotifikaceService.aktivujUdalost(id, params),

        onSuccess: (result, { id }) => {
            queryClient.invalidateQueries({ queryKey: ["aktivniPoplach"] });
            queryClient.invalidateQueries({ queryKey: ["udalosti"] });
            queryClient.invalidateQueries({ queryKey: ["dashboardStats"] });
            // Invalidate detail query for both old and new event (if created)
            queryClient.invalidateQueries({ queryKey: ["udalosti", "detail", id] });
            if (result.udalost?.id && result.udalost.id !== id) {
                queryClient.invalidateQueries({ queryKey: ["udalosti", "detail", result.udalost.id] });
            }
            showToast(result.message || "Poplach byl úspěšně aktivován.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktivovat poplach.", "error");
        },
    });
};

export const useResendNotifikaceMutation = () => {
    const { showToast } = useToast();

    return useMutation<
        { webPushOdeslano: number; fcmOdeslano: number; message: string },
        Error,
        { id: number; webPush?: boolean; fcm?: boolean; userIds?: number[] | null }
    >({
        mutationFn: ({ id, webPush, fcm, userIds }) =>
            JsdhNotifikaceService.resendNotifikace(id, { webPush, fcm, userIds }),

        onSuccess: (result) => {
            showToast(result.message || "Notifikace byly odeslány.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odeslat notifikace.", "error");
        },
    });
};

export const useTestWebPushMutation = () => {
    const { showToast } = useToast();

    return useMutation<{ message: string; odeslano: number }, Error, void>({
        mutationFn: () => JsdhNotifikaceService.testWebPush(),

        onSuccess: (result) => {
            showToast(result.message || `Testovací notifikace odeslána (${result.odeslano}).`, "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odeslat testovací notifikaci.", "error");
        },
    });
};

// -----------------------------
// TYPY UDÁLOSTÍ QUERIES
// -----------------------------

export const useTypyUdalostiQuery = () => {
    return useQuery<TypUdalosti[], Error>({
        queryKey: ["typyUdalosti"],
        queryFn: () => AdminService.getTypyUdalosti(),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const usePodtypyByTypIdQuery = (typId: number | undefined) => {
    return useQuery<PodtypUdalosti[], Error>({
        queryKey: ["podtypyUdalosti", typId],
        queryFn: () => AdminService.getPodtypyByTypId(typId!),
        enabled: !!typId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useAllPodtypyQuery = () => {
    return useQuery<PodtypUdalosti[], Error>({
        queryKey: ["vsechnyPodtypyUdalosti"],
        queryFn: () => AdminService.getAllPodtypy(),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// -----------------------------
// UDÁLOSTI (SEZNAM A DETAIL) QUERIES
// -----------------------------

export const useSearchUdalostiQuery = (params: SearchUdalostiParams) => {
    return useQuery<PagedResponseUdalost, Error>({
        queryKey: ["udalosti", "search", params],
        queryFn: () => JsdhNotifikaceService.searchUdalosti(params),
    });
};

export const useUdalostDetailQuery = (id: number) => {
    return useQuery<UdalostDetail, Error>({
        queryKey: ["udalosti", "detail", id],
        queryFn: () => JsdhNotifikaceService.getUdalostDetail(id),
        enabled: !!id,
    });
};

// -----------------------------
// JEDNOTKA QUERIES & MUTATIONS
// -----------------------------

export const useJednotkaQuery = () => {
    return useQuery<JednotkaDto, Error>({
        queryKey: ["jednotka"],
        queryFn: () => JsdhNotifikaceService.getJednotka(),
        enabled: !isMockActive(),
    });
};

export const useUpdateJednotkaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<JednotkaDto, Error, JednotkaDto>({
        mutationFn: (data) => JsdhNotifikaceService.updateJednotka(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["jednotka"] });
            showToast("Údaje o jednotce byly uloženy.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se uložit údaje o jednotce.", "error");
        },
    });
};

export const useUploadLogoMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, File>({
        mutationFn: (file) => JsdhNotifikaceService.uploadJednotkaLogo(file),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["jednotka"] });
            showToast("Logo bylo nahráno.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se nahrát logo.", "error");
        },
    });
};

export const useDeleteLogoMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, void>({
        mutationFn: () => JsdhNotifikaceService.deleteJednotkaLogo(),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["jednotka"] });
            showToast("Logo bylo smazáno.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat logo.", "error");
        },
    });
};

// -----------------------------
// USER SEARCH QUERY
// -----------------------------

export const useSearchUsersQuery = (query?: string) => {
    return useQuery<Uzivatel[], Error>({
        queryKey: ["adminUsers", "search", query],
        queryFn: () => AdminService.searchUsers(query),
        enabled: query !== undefined,
    });
};

// -----------------------------
// PIN QUERIES & MUTATIONS
// -----------------------------

export const useUserPinQuery = (id: number, enabled: boolean = false) => {
    return useQuery<{ pin: string; hasPin: boolean }, Error>({
        queryKey: ["adminUserPin", id],
        queryFn: () => AdminService.getUserPin(id),
        enabled: enabled && !!id, // HO-113: fetch až na vyžádání (klik na oko)
    });
};

export const useSetUserPinMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number; pin: string }>({
        mutationFn: ({ id, pin }) => AdminService.setUserPin(id, pin),

        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ["adminUserPin", id] });
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            queryClient.invalidateQueries({ queryKey: ["adminUser", id] });
            showToast("PIN byl nastaven.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se nastavit PIN.", "error");
        },
    });
};

export const useDeleteUserPinMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => AdminService.deleteUserPin(id),

        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ["adminUserPin", id] });
            // HO-113: hasPin řídí zobrazení buňky — seznam i detail musí přestat nabízet reveal
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            queryClient.invalidateQueries({ queryKey: ["adminUser", id] });
            showToast("PIN byl smazán.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat PIN.", "error");
        },
    });
};

export const useBulkSetPinMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ message: string }, Error, BulkSetPinRequest>({
        mutationFn: (data) => AdminService.bulkSetPin(data),

        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            // HO-113: zneplatnit i všechny odhalené PINy (prefix), jinak by oko ukazovalo starou hodnotu
            queryClient.invalidateQueries({ queryKey: ["adminUserPin"] });
            showToast(result.message || "PIN byl hromadně nastaven.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se hromadně nastavit PIN.", "error");
        },
    });
};

export const useBulkDeletePinMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ message: string }, Error, BulkDeletePinRequest>({
        mutationFn: (data) => AdminService.bulkDeletePin(data),

        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            // HO-113: zneplatnit i všechny odhalené PINy (prefix), ať v cache nezůstane plaintext smazaného PINu
            queryClient.invalidateQueries({ queryKey: ["adminUserPin"] });
            showToast(result.message || "PIN byl hromadně smazán.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se hromadně smazat PIN.", "error");
        },
    });
};

// -----------------------------
// STATISTIKY QUERIES
// -----------------------------

export const useDashboardStatsQuery = (params?: StatsFilterParams) => {
    return useQuery<DashboardStats, Error>({
        queryKey: ["dashboardStats", params],
        queryFn: () => JsdhNotifikaceService.getDashboardStats(params),
    });
};

export const useStatistikaQuery = () => {
    return useQuery<StatistikaResponse, Error>({
        queryKey: ["statistika"],
        queryFn: () => JsdhNotifikaceService.getStatistika(),
    });
};

export const useStatistikaDatumQuery = (datum: string) => {
    return useQuery({
        queryKey: ["statistikaDatum", datum],
        queryFn: () => JsdhNotifikaceService.getStatistikaDatum(datum),
        enabled: !!datum,
    });
};

// -----------------------------
// UDÁLOSTI QUERIES
// -----------------------------

export const useUdalostiTypesQuery = () => {
    return useQuery<string[], Error>({
        queryKey: ["udalostiTypes"],
        queryFn: () => JsdhNotifikaceService.getUdalostiTypes(),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useAktivniPosledniHodinaQuery = () => {
    return useQuery<Udalost[], Error>({
        queryKey: ["udalostiAktivniPosledniHodina"],
        queryFn: () => JsdhNotifikaceService.getAktivniPosledniHodina(),
    });
};

// -----------------------------
// POPLACH STATUS QUERY
// -----------------------------

export const usePoplachStatusQuery = () => {
    return useQuery<AlertStatus, Error>({
        queryKey: ["poplachStatus"],
        queryFn: () => JsdhNotifikaceService.getPoplachStatus(),
    });
};

// -----------------------------
// PRINT QUERIES & MUTATIONS
// -----------------------------

export const usePrintStatusQuery = (id: number) => {
    return useQuery<{ printAvailable: boolean; enabledDevicesCount: number; alreadyQueued: boolean }, Error>({
        queryKey: ["printStatus", id],
        queryFn: () => JsdhNotifikaceService.getPrintStatus(id),
        enabled: !!id,
    });
};

export const usePrintUdalostMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean; message: string }, Error, number>({
        mutationFn: (id) => JsdhNotifikaceService.printUdalost(id),

        onSuccess: (result, id) => {
            queryClient.invalidateQueries({ queryKey: ["printStatus", id] });
            showToast(result.message || "Tisková úloha byla zařazena do fronty.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zařadit tiskovou úlohu.", "error");
        },
    });
};

// -----------------------------
// ŠABLONY UDÁLOSTÍ QUERIES & MUTATIONS
// -----------------------------

export const useSablonyQuery = () => {
    return useQuery<SablonaUdalosti[], Error>({
        queryKey: ["sablonyUdalosti"],
        queryFn: () => AdminService.getSablonyUdalosti(),
        staleTime: 5 * 60 * 1000,
    });
};

export const useAllSablonyQuery = () => {
    return useQuery<SablonaUdalosti[], Error>({
        queryKey: ["sablonyUdalosti", "all"],
        queryFn: () => AdminService.getAllSablonyUdalosti(),
    });
};

export const useCreateSablonaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SablonaUdalosti, Error, CreateSablonaRequest>({
        mutationFn: (data) => AdminService.createSablona(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["sablonyUdalosti"] });
            queryClient.invalidateQueries({ queryKey: ["sablonyUdalosti", "all"] });
            showToast("Šablona byla úspěšně vytvořena.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit šablonu.", "error");
        },
    });
};

export const useUpdateSablonaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SablonaUdalosti, Error, { id: number; data: UpdateSablonaRequest }>({
        mutationFn: ({ id, data }) => AdminService.updateSablona(id, data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["sablonyUdalosti"] });
            queryClient.invalidateQueries({ queryKey: ["sablonyUdalosti", "all"] });
            showToast("Šablona byla úspěšně aktualizována.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat šablonu.", "error");
        },
    });
};

export const useDeleteSablonaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ message: string }, Error, number>({
        mutationFn: (id) => AdminService.deleteSablona(id),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["sablonyUdalosti"] });
            queryClient.invalidateQueries({ queryKey: ["sablonyUdalosti", "all"] });
            showToast("Šablona byla úspěšně smazána.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat šablonu.", "error");
        },
    });
};

export const useToggleSablonaStatusMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ message: string }, Error, { id: number; aktivni: boolean }>({
        mutationFn: ({ id, aktivni }) => AdminService.toggleSablonaStatus(id, aktivni),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["sablonyUdalosti"] });
            queryClient.invalidateQueries({ queryKey: ["sablonyUdalosti", "all"] });
            showToast(
                `Šablona byla ${variables.aktivni ? "aktivována" : "deaktivována"}.`,
                "success"
            );
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se změnit stav šablony.", "error");
        },
    });
};

export const useVyhlasitZeSablonyMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<CreateUdalostResponse, Error, { id: number; data?: VyhlasitZeSablonyRequest }>({
        mutationFn: ({ id, data }) => AdminService.vyhlasitZeSablony(id, data),

        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["aktivniPoplach"] });
            queryClient.invalidateQueries({ queryKey: ["udalosti"] });
            showToast(response.message || "Poplach byl úspěšně vyhlášen ze šablony.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vyhlásit poplach ze šablony.", "error");
        },
    });
};
